import { useMemo, useState } from 'react';
import clsx from 'clsx';
import { ArrowDown, ArrowUp, ArrowUpDown, Search } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../../../components/ui/select';
import type { PassViewRecord } from '../../../utils/fetchWeldViewApis';
import {
  buildPassFilterOptions,
  emptyProductionPassFilters,
  filterPassViewRecords,
  type ProductionPassFilterState,
} from '../../../utils/productionPassFilters';
import {
  deriveBinaryPassStatus,
  formatBugTypeDisplay,
  formatPassDurationDisplay,
  formatPassTimestampDisplay,
  parsePassUnixSeconds,
  passStatusBadgeClass,
  sortPassesByStartTime,
} from '../../../utils/passViewFormatters';
import {
  violationsForPassName,
  type WpsViolationRecord,
} from '../../../utils/wpsViolationModel';

export type PassSortKey = 'name' | 'start' | 'duration' | 'status';

interface PassSummaryTablePanelProps {
  passes: PassViewRecord[];
  selectedPassKey: string | null;
  onSelectPass: (pass: PassViewRecord) => void;
  statusCalculationMethod?: string;
  outOfLimitContext?: boolean;
  violations?: WpsViolationRecord[];
}

export function passRowKey(pass: PassViewRecord): string {
  return `${pass.id}::${pass.name ?? ''}::${pass.startTime ?? ''}`;
}

function passDurationSec(pass: PassViewRecord): number {
  const start = parsePassUnixSeconds(pass.startTime);
  const end = parsePassUnixSeconds(pass.endTime);
  if (start == null || end == null || end < start) return -1;
  return (end - start) / 1000;
}

function SortIcon({ active, dir }: { active: boolean; dir: 'asc' | 'desc' }) {
  if (!active) return <ArrowUpDown className="ml-1 inline h-3 w-3 opacity-40" />;
  return dir === 'asc' ? (
    <ArrowUp className="ml-1 inline h-3 w-3" />
  ) : (
    <ArrowDown className="ml-1 inline h-3 w-3" />
  );
}

function filtersAreActive(filters: ProductionPassFilterState): boolean {
  return Boolean(
    filters.passNameFilter.trim() ||
      filters.stationFilter ||
      filters.bugTypeFilter.length ||
      filters.statusFilter.length ||
      filters.welderIdFilter.length ||
      filters.dateRange.dateFrom ||
      filters.dateRange.dateTo,
  );
}

export const PassSummaryTablePanel = ({
  passes,
  selectedPassKey,
  onSelectPass,
  statusCalculationMethod = 'Instantaneous',
  outOfLimitContext = false,
  violations = [],
}: PassSummaryTablePanelProps) => {
  const [filters, setFilters] = useState<ProductionPassFilterState>(emptyProductionPassFilters());
  const [sortKey, setSortKey] = useState<PassSortKey>('start');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');

  const sortedBase = useMemo(() => sortPassesByStartTime(passes), [passes]);
  const filterOptions = useMemo(
    () => buildPassFilterOptions(sortedBase, statusCalculationMethod),
    [sortedBase, statusCalculationMethod],
  );

  const filteredPasses = useMemo(
    () => filterPassViewRecords(sortedBase, filters, statusCalculationMethod),
    [sortedBase, filters, statusCalculationMethod],
  );

  const displayPasses = useMemo(() => {
    const rows = [...filteredPasses];
    const mul = sortDir === 'asc' ? 1 : -1;
    rows.sort((a, b) => {
      if (sortKey === 'name') {
        return mul * String(a.name ?? '').localeCompare(String(b.name ?? ''));
      }
      if (sortKey === 'start') {
        const as = parsePassUnixSeconds(a.startTime) ?? 0;
        const bs = parsePassUnixSeconds(b.startTime) ?? 0;
        return mul * (as - bs);
      }
      if (sortKey === 'duration') {
        return mul * (passDurationSec(a) - passDurationSec(b));
      }
      return mul * deriveBinaryPassStatus(a).localeCompare(deriveBinaryPassStatus(b));
    });
    return rows;
  }, [filteredPasses, sortDir, sortKey]);

  const toggleSort = (key: PassSortKey) => {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
      return;
    }
    setSortKey(key);
    setSortDir('asc');
  };

  const updateFilters = (partial: Partial<ProductionPassFilterState>) => {
    setFilters((prev) => ({ ...prev, ...partial }));
  };

  const clearFilters = () => {
    setFilters(emptyProductionPassFilters());
  };

  const filtersActive = filtersAreActive(filters);

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-xl border border-[#E2E5EA] bg-white">
      <div className="shrink-0 space-y-2 border-b border-[#E2E5EA] bg-page-bg px-3 py-2">
        <div className="relative">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-heritage-grey" />
          <input
            type="search"
            value={filters.passNameFilter}
            onChange={(e) => updateFilters({ passNameFilter: e.target.value })}
            placeholder="Search pass…"
            className="box-border h-9 w-full rounded-lg border border-[#E2E5EA] bg-white py-0 pl-8 pr-3 text-sm text-text-grey placeholder:text-heritage-grey focus:border-blueprint-blue focus:outline-none focus:ring-1 focus:ring-blueprint-blue/20"
          />
        </div>
        <div className="grid grid-cols-2 gap-2 lg:grid-cols-4">
          <Select
            value={filters.statusFilter[0] ?? 'all'}
            onValueChange={(v) =>
              updateFilters({ statusFilter: v === 'all' ? [] : [v] })
            }
          >
            <SelectTrigger className="h-8 w-full text-xs">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Status: All</SelectItem>
              {filterOptions.statuses.map((s) => (
                <SelectItem key={s} value={s}>
                  Status: {s}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select
            value={filters.stationFilter || 'all'}
            onValueChange={(v) =>
              updateFilters({ stationFilter: v === 'all' ? '' : v })
            }
          >
            <SelectTrigger className="h-8 w-full text-xs">
              <SelectValue placeholder="Station" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Station: All</SelectItem>
              {filterOptions.stations.map((s) => (
                <SelectItem key={s} value={s}>
                  Station: {s}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select
            value={filters.welderIdFilter[0] ?? 'all'}
            onValueChange={(v) =>
              updateFilters({ welderIdFilter: v === 'all' ? [] : [v] })
            }
          >
            <SelectTrigger className="h-8 w-full text-xs">
              <SelectValue placeholder="Welder" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Welder: All</SelectItem>
              {filterOptions.welderIds.map((id) => (
                <SelectItem key={id} value={id}>
                  Welder: {id}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select
            value={filters.bugTypeFilter[0] ?? 'all'}
            onValueChange={(v) =>
              updateFilters({ bugTypeFilter: v === 'all' ? [] : [v] })
            }
          >
            <SelectTrigger className="h-8 w-full text-xs">
              <SelectValue placeholder="CW/CCW" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">CW/CCW: All</SelectItem>
              {filterOptions.bugTypes.map((t) => (
                <SelectItem key={t} value={t}>
                  CW/CCW: {formatBugTypeDisplay(t)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <p className="text-[11px] text-heritage-grey">
            Showing {displayPasses.length} of {sortedBase.length} passes
          </p>
          {filtersActive ? (
            <button
              type="button"
              data-testid="clear-pass-filters"
              onClick={clearFilters}
              className="text-[11px] font-semibold text-blueprint-blue hover:underline"
            >
              Clear filters
            </button>
          ) : null}
        </div>
      </div>

      <div className="min-h-0 flex-1 overflow-auto">
        <table className="min-w-full text-left text-sm">
          <thead className="sticky top-0 z-10 bg-page-bg text-[10px] font-bold uppercase tracking-wider text-heritage-grey">
            <tr>
              <th className="w-10 px-3 py-2">#</th>
              {(
                [
                  ['name', 'Pass'],
                  ['start', 'Start'],
                  ['duration', 'Duration'],
                  ['status', 'Status'],
                ] as const
              ).map(([key, label]) => (
                <th key={key} className="px-3 py-2">
                  <button
                    type="button"
                    onClick={() => toggleSort(key)}
                    className="inline-flex items-center font-bold uppercase tracking-wider hover:text-blueprint-blue"
                  >
                    {label}
                    <SortIcon active={sortKey === key} dir={sortDir} />
                  </button>
                </th>
              ))}
              <th className="px-3 py-2">Welder</th>
              <th className="px-3 py-2">Station</th>
            </tr>
          </thead>
          <tbody>
            {displayPasses.map((pass, idx) => {
              const key = passRowKey(pass);
              const status = deriveBinaryPassStatus(pass);
              const passViolations = violationsForPassName(violations, pass.name || '');
              const hasViolation = outOfLimitContext && passViolations.length > 0;
              const selected = selectedPassKey === key;

              return (
                <tr
                  key={`${key}-${idx}`}
                  data-testid="pass-summary-row"
                  data-from-violations={hasViolation ? 'true' : undefined}
                  data-selected={selected ? 'true' : undefined}
                  className={clsx(
                    'cursor-pointer border-t border-[#E2E5EA]',
                    hasViolation && 'bg-[rgba(196,0,31,0.04)]',
                    selected
                      ? 'bg-[rgba(0,102,179,0.08)]'
                      : 'hover:bg-[rgba(0,39,63,0.04)]',
                  )}
                  onClick={() => onSelectPass(pass)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      onSelectPass(pass);
                    }
                  }}
                  tabIndex={0}
                  role="button"
                  aria-label={`View detail for pass ${pass.name || 'unknown'}`}
                >
                  <td className="px-3 py-2 tabular-nums text-heritage-grey">{idx + 1}</td>
                  <td className="px-3 py-2 font-medium text-blueprint-blue">
                    {pass.name || '—'}
                  </td>
                  <td className="whitespace-nowrap px-3 py-2 text-text-grey">
                    {formatPassTimestampDisplay(pass.startTime)}
                  </td>
                  <td className="px-3 py-2 text-text-grey">
                    {formatPassDurationDisplay(pass)}
                  </td>
                  <td className="px-3 py-2">
                    <span
                      className={clsx(
                        'inline-flex rounded-full px-2 py-0.5 text-[11px] font-semibold',
                        passStatusBadgeClass(status),
                      )}
                    >
                      {status}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-text-grey">{pass.welderId || '—'}</td>
                  <td className="px-3 py-2 text-text-grey">
                    {pass.stationNumber?.trim() || '—'}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {displayPasses.length === 0 && (
          <p className="px-3 py-4 text-sm text-heritage-grey">
            No passes match the current filters.
          </p>
        )}
      </div>
    </div>
  );
};
